/**
 * SYSTELOS MONITOR - Serviço de Clientes (Frontend)
 *
 * @version 2.6.0
 * @date 26/01/2026
 * FIX: Rotas toggle DEC e DJE alinhadas com backend
 * FIX: Upload certificado usa rota correta
 * FIX: Tipo de ativo aceita string ('todos', 'true', 'false') ou boolean
 */

import api from './api'

// Interface do histórico de consultas
export interface HistoricoConsulta {
  id: string
  cliente_id: string
  tipo: 'DJE' | 'DEC_SEFAZ'
  data_consulta: string
  sucesso: boolean
  status?: 'PROCESSANDO' | 'SUCESSO' | 'ERRO'
  estrategia?: string
  comunicacoes_encontradas: number
  novas_comunicacoes: number
  erro?: string
  tempo_execucao_ms?: number
}

// Interface de resposta paginada
interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

// Filtros de clientes
export interface ClienteListarParams {
  page?: number
  limit?: number
  search?: string
  busca?: string
  ativo?: boolean | string | 'todos' | 'true' | 'false'
}

// Funções do serviço
const clientesService = {
  // Listar clientes
  async listar(params?: ClienteListarParams) {
    // Converter ativo string para boolean se necessário
    const queryParams: Record<string, any> = { ...params }
    
    if (params?.ativo !== undefined) {
      if (params.ativo === 'todos' || params.ativo === undefined) {
        delete queryParams.ativo
      } else if (params.ativo === 'true' || params.ativo === true) {
        queryParams.ativo = true
      } else if (params.ativo === 'false' || params.ativo === false) {
        queryParams.ativo = false
      }
    }

    // Usar busca ou search
    if (params?.busca) {
      queryParams.search = params.busca
      delete queryParams.busca
    }

    const response = await api.get('/clientes', { params: queryParams })
    return response.data
  },

  // Buscar cliente por ID
  async buscarPorId(id: string) {
    const response = await api.get(`/clientes/${id}`)
    // Backend retorna { success: true, data: {...} }
    return response.data.data
  },

  // Criar cliente
  async criar(data: any) {
    const response = await api.post('/clientes', data)
    return response.data
  },

  // Atualizar cliente
  async atualizar(id: string, data: any) {
    const response = await api.put(`/clientes/${id}`, data)
    return response.data
  },

  // Deletar cliente
  async deletar(id: string) {
    const response = await api.delete(`/clientes/${id}`)
    return response.data
  },

  // ============================================
  // CERTIFICADO DEC
  // ============================================

  // Upload certificado (rota do módulo certificados)
  async uploadCertificado(id: string, arquivo: File, senha: string) {
    const formData = new FormData()
    formData.append('certificado', arquivo)
    formData.append('senha', senha)
    const response = await api.post(`/clientes/${id}/certificado`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  // Remover certificado
  async removerCertificado(id: string) {
    const response = await api.delete(`/clientes/${id}/certificado`)
    return response.data
  },

  // ============================================
  // DEC (SEFAZ-SP)
  // ============================================

  // Toggle DEC ativo - CORRIGIDO: rota é PUT /clientes/:id/dec (sem /toggle)
  async toggleDec(id: string, ativo: boolean) {
    const response = await api.put(`/clientes/${id}/dec`, { ativo })
    return response.data
  },

  // Consultar DEC (ASSÍNCRONO - retorna imediatamente)
  async consultarDec(id: string) {
    const response = await api.post(`/clientes/${id}/dec/consultar`)
    return response.data
  },

  // Verificar status de consulta DEC
  async verificarStatusDec(id: string, historicoId: string) {
    const response = await api.get(`/clientes/${id}/dec/status/${historicoId}`)
    return response.data
  },

  // ============================================
  // DJE (Domicílio Judicial Eletrônico)
  // ============================================

  // Atualizar configuração DJE (tenant_id + ativo)
  async atualizarDJE(id: string, data: { dje_tenant_id?: string | null; dje_ativo?: boolean }) {
    const response = await api.put(`/clientes/${id}/credenciais-dje`, data)
    return response.data
  },

  // Toggle DJE ativo - usa mesma rota de atualizar com apenas o campo ativo
  async toggleDje(id: string, ativo: boolean) {
    const response = await api.put(`/clientes/${id}/credenciais-dje`, { dje_ativo: ativo })
    return response.data
  },

  // Testar conexão DJE
  async testarDJE(id: string) {
    const response = await api.post(`/clientes/${id}/dje/testar`)
    return response.data
  },

  // Consultar DJE
  async consultarDJE(id: string) {
    const response = await api.post(`/clientes/${id}/dje/consultar`)
    return response.data
  },

  // ============================================
  // NOTIFICAÇÕES
  // ============================================

  // Atualizar notificações
  async atualizarNotificacoes(id: string, data: any) {
    const response = await api.put(`/clientes/${id}/notificacoes`, data)
    return response.data
  },

  // ============================================
  // HISTÓRICO
  // ============================================

  // Listar histórico de consultas
  async listarHistorico(id: string, page: number = 1, limit: number = 10): Promise<PaginatedResponse<HistoricoConsulta>> {
    const response = await api.get(`/clientes/${id}/historico`, {
      params: { page, limit }
    })
    return response.data
  },

  // ============================================
  // ESTATÍSTICAS
  // ============================================

  // Estatísticas
  async stats() {
    const response = await api.get('/clientes/stats')
    return response.data
  }
}

export default clientesService

